import API from "../lib/api";

export const erpApi = {
  login: (payload) => API.post("/auth/login", payload),
  signup: (payload) => API.post("/auth/signup", payload),
  logout: () => API.post("/auth/logout"),
  forgotPassword: (payload) => API.post("/auth/forgot-password", payload),
  resetPassword: (payload) => API.post("/auth/reset-password", payload),
  me: () => API.get("/auth/me"),
  clinic: () => API.get("/auth/clinic"),
  updateClinic: (payload) => API.patch("/auth/clinic", payload),
  dashboard: () => API.get("/reports/summary"),
  analytics: () => API.get("/reports/analytics"),
  patients: (params) => API.get("/patients", { params }),
  checkPatientDuplicate: (mobile) => API.get("/patients/duplicates/check", { params: { mobile } }),
  patient: (id) => API.get(`/patients/${id}`),
  createPatient: (payload) => API.post("/patients", payload),
  updatePatient: (id, payload) => API.patch(`/patients/${id}`, payload),
  deletePatient: (id) => API.delete(`/patients/${id}`),
  addPatientDocument: (id, payload) => API.post(`/patients/${id}/documents`, payload),
  deletePatientDocument: (patientId, documentId) => API.delete(`/patients/${patientId}/documents/${documentId}`),
  doctors: (params) => API.get("/doctors", { params }),
  createDoctor: (payload) => API.post("/doctors", payload),
  updateDoctor: (id, payload) => API.patch(`/doctors/${id}`, payload),
  deleteDoctor: (id) => API.delete(`/doctors/${id}`),
  appointments: (params) => API.get("/doctors/appointments/list", { params }),
  createAppointment: (payload) => API.post("/doctors/appointments", payload),
  updateAppointment: (id, payload) => API.patch(`/doctors/appointments/${id}`, payload),
  deleteAppointment: (id) => API.delete(`/doctors/appointments/${id}`),
  services: (params) => API.get("/services", { params }),
  createService: (payload) => API.post("/services", payload),
  updateService: (id, payload) => API.patch(`/services/${id}`, payload),
  deleteService: (id) => API.delete(`/services/${id}`),
  bootstrapServices: () => API.post("/services/bootstrap-defaults"),
  bills: (params) => API.get("/bills", { params }),
  createBill: (payload) => API.post("/bills", payload),
  updateBill: (id, payload) => API.patch(`/bills/${id}`, payload),
  billPdf: (id) => API.get(`/bills/${id}/pdf`, { responseType: "blob" }),
  addBillPayment: (id, payload) => API.post(`/bills/${id}/payments`, payload),
  deleteBill: (id) => API.delete(`/bills/${id}`),
  prescriptions: (params) => API.get("/prescriptions", { params }),
  createPrescription: (payload) => API.post("/prescriptions", payload),
  deletePrescription: (id) => API.delete(`/prescriptions/${id}`),
  samples: (params) => API.get("/samples", { params }),
  createSample: (payload) => API.post("/samples", payload),
  updateSample: (id, payload) => API.patch(`/samples/${id}`, payload),
  saveSampleReportText: (id, payload) => API.post(`/samples/${id}/report-text`, payload),
  extractSampleReport: (id, formData) => API.post(`/samples/${id}/extract-report`, formData),
  sampleLetterheadPdf: (id) => API.get(`/samples/${id}/letterhead-pdf`, { responseType: "blob" }),
  sampleStandardizedPdf: (id) => API.get(`/samples/${id}/standardized-pdf`, { responseType: "blob" }),
  standardizeSampleReport: (id, formData) => API.post(`/samples/${id}/standardize-report`, formData),
  standardizeSampleText: (id, payload) => API.post(`/samples/${id}/standardize-text`, payload),
  standardizePreview: (formData) => API.post("/samples/standardize/preview", formData),
  standardizePreviewPdf: (formData) => API.post("/samples/standardize/preview-pdf", formData, { responseType: "blob" }),
  standardizeUploadAndGenerate: (formData) => API.post("/samples/standardize/upload-and-generate", formData, { responseType: "blob" }),
  standardizeGeneratePdf: (payload) => API.post("/samples/standardize/generate-pdf", payload, { responseType: "blob" }),
  attachLabReport: (payload) => API.post("/samples/report-webhook", payload),
  employees: (params) => API.get("/employees", { params }),
  employee: (id) => API.get(`/employees/${id}`),
  createEmployee: (payload) => API.post("/employees", payload),
  updateEmployee: (id, payload) => API.patch(`/employees/${id}`, payload),
  deleteEmployee: (id) => API.delete(`/employees/${id}`),
  generateEmployeeQr: (employee_id) => API.post("/employees/generate-qr", { employee_id }),
  attendance: (params) => API.get("/attendance", { params }),
  attendanceToday: () => API.get("/attendance/today"),
  attendanceHistory: (params) => API.get("/attendance/history", { params }),
  attendanceExport: () => API.get("/attendance/export", { responseType: "blob" }),
  scanAttendance: (payload) => API.post("/attendance/scan", payload),
  createManualAttendance: (payload) => API.post("/attendance/manual", payload),
  updateAttendance: (id, payload) => API.patch(`/attendance/${id}`, payload),
  payrolls: (params) => API.get("/payrolls", { params }),
  createPayroll: (payload) => API.post("/payrolls", payload),
  expenses: (params) => API.get("/expenses", { params }),
  createExpense: (payload) => API.post("/expenses", payload),
  updateExpense: (id, payload) => API.patch(`/expenses/${id}`, payload),
  deleteExpense: (id) => API.delete(`/expenses/${id}`),
  importTemplateUrl: (entity) => `/imports/templates/${entity}`,
  importCsv: (entity, formData, dryRun = true) => API.post(`/imports/csv/${entity}`, formData, { params: { dry_run: dryRun } }),
  importReportPdfs: (formData, dryRun = true) => API.post("/imports/report-pdfs", formData, { params: { dry_run: dryRun } }),
  importBillPdfs: (formData, dryRun = true) => API.post("/imports/bill-pdfs", formData, { params: { dry_run: dryRun } }),
};

export const formatCurrency = (amount = 0) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

export const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const hasTimePart = (value) => {
  const raw = String(value || "").trim();
  return /T\d{2}:\d{2}/.test(raw) || /\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(raw);
};

export const formatTime = (value) => {
  if (!value || !hasTimePart(value)) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export const formatDateTime = (value) => {
  if (!value) return "-";
  const date = formatDate(value);
  const time = formatTime(value);
  return time === "-" ? date : `${date}, ${time}`;
};

export const normalizeExternalUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^(https?:|blob:|data:)/i.test(raw)) return raw;
  if (raw.startsWith("//")) return `https:${raw}`;
  return `https://${raw.replace(/^\/+/, "")}`;
};
